import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import './UserManagement.css';

/* ─────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────── */

const ROLES = [
  { value: 'super_admin',   label: 'Super Admin',   color: '#7c3aed' },
  { value: 'system_admin',  label: 'System Admin',  color: '#d97706' },
  { value: 'ceo',           label: 'CEO',           color: '#1d4ed8' },
  { value: 'chro',          label: 'CHRO',          color: '#0891b2' },
  { value: 'function_head', label: 'Function Head', color: '#059669' },
  { value: 'individual',    label: 'Individual',    color: '#64748b' },
];

const ROLE_MAP = Object.fromEntries(ROLES.map(r => [r.value, r]));

function getRoleInfo(role) {
  return ROLE_MAP[role] ?? { label: role ?? 'Unknown', color: '#94a3b8' };
}

function initials(firstName, lastName, email) {
  if (firstName || lastName) {
    return `${(firstName || '')[0] ?? ''}${(lastName || '')[0] ?? ''}`.toUpperCase();
  }
  return (email?.[0] ?? '?').toUpperCase();
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/* ─────────────────────────────────────────────────────────────
   STAT CARD
───────────────────────────────────────────────────────────── */
function StatCard({ label, value, icon, accent }) {
  return (
    <div className="um-stat-card" style={{ borderTopColor: accent }}>
      <div className="um-stat-icon" style={{ background: accent + '18', color: accent }}>
        <i className={`bi ${icon}`} />
      </div>
      <div className="um-stat-body">
        <div className="um-stat-value">{value}</div>
        <div className="um-stat-label">{label}</div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   ROLE BADGE
───────────────────────────────────────────────────────────── */
function RoleBadge({ role }) {
  const info = getRoleInfo(role);
  return (
    <span
      className="um-role-badge"
      style={{ background: info.color + '18', color: info.color, borderColor: info.color + '40' }}
    >
      {info.label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────
   EDIT ROLE MODAL
───────────────────────────────────────────────────────────── */
function EditRoleModal({ user, currentUserId, superAdminCount, onSave, onClose, saving }) {
  const [selected, setSelected] = useState(user.role ?? 'individual');

  const isSelf = user.id === currentUserId;
  const isLastSuperAdmin =
    user.role === 'super_admin' && superAdminCount <= 1;
  const unchanged = selected === user.role;

  const canSave = !unchanged && !saving;

  const displayName =
    user.first_name || user.last_name
      ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
      : user.email;

  return (
    <div className="um-modal-overlay" onClick={onClose}>
      <div className="um-modal" onClick={e => e.stopPropagation()}>
        <div className="um-modal-header">
          <h3>Change Role</h3>
          <button className="um-modal-close" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="um-modal-user">
          <div
            className="um-avatar um-avatar--lg"
            style={{ background: getRoleInfo(user.role).color }}
          >
            {initials(user.first_name, user.last_name, user.email)}
          </div>
          <div>
            <div className="um-modal-name">{displayName}</div>
            <div className="um-modal-email">{user.email}</div>
          </div>
        </div>

        {isSelf && (
          <div className="um-alert um-alert--warning">
            <i className="bi bi-exclamation-triangle" />
            You are editing your own account. Changes take effect after your next login.
          </div>
        )}

        {isLastSuperAdmin && selected !== 'super_admin' && (
          <div className="um-alert um-alert--danger">
            <i className="bi bi-shield-exclamation" />
            This is the only Super Admin. Downgrading will lock you out of admin features.
          </div>
        )}

        <div className="um-modal-label">Select new role</div>
        <div className="um-role-grid">
          {ROLES.map(r => (
            <button
              key={r.value}
              className={`um-role-option ${selected === r.value ? 'selected' : ''}`}
              style={selected === r.value ? { borderColor: r.color, background: r.color + '12' } : {}}
              onClick={() => setSelected(r.value)}
            >
              <span className="um-role-dot" style={{ background: r.color }} />
              {r.label}
              {r.value === user.role && (
                <span className="um-role-current">current</span>
              )}
            </button>
          ))}
        </div>

        <div className="um-modal-footer">
          <button className="um-btn um-btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="um-btn um-btn--primary"
            onClick={() => onSave(user.id, selected)}
            disabled={!canSave}
          >
            {saving ? (
              <><i className="bi bi-arrow-repeat um-spin" /> Saving…</>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const UserManagement = () => {
  const { user: currentUser } = useAuth();

  const [users, setUsers]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [toast, setToast]         = useState('');

  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving]         = useState(false);

  /* ── Fetch all users ── */
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError('');
      try {
        const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
        const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
        const storedSession = sessionStorage.getItem('supabase-auth-token');
        const accessToken = storedSession
          ? (JSON.parse(storedSession).access_token ?? supabaseKey)
          : supabaseKey;

        const res = await fetch(
          `${supabaseUrl}/rest/v1/profiles?select=id,email,role,first_name,last_name,created_at,organization_id&order=created_at.asc`,
          {
            headers: {
              apikey: supabaseKey,
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.message ?? `HTTP ${res.status}`);
        }

        setUsers(await res.json());
      } catch (err) {
        console.error('UserManagement fetch error:', err);
        setError('Failed to load users. ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  /* ── Derived stats ── */
  const stats = useMemo(() => {
    const total       = users.length;
    const superAdmins = users.filter(u => u.role === 'super_admin').length;
    const breakdown   = ROLES.map(r => ({
      ...r,
      count: users.filter(u => u.role === r.value).length,
    })).filter(r => r.count > 0);
    return { total, superAdmins, breakdown };
  }, [users]);

  /* ── Filtered list ── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(u => {
      const matchRole = roleFilter === 'all' || u.role === roleFilter;
      const matchSearch =
        !q ||
        (u.email ?? '').toLowerCase().includes(q) ||
        (u.first_name ?? '').toLowerCase().includes(q) ||
        (u.last_name  ?? '').toLowerCase().includes(q);
      return matchRole && matchSearch;
    });
  }, [users, search, roleFilter]);

  /* ── Role update ── */
  const handleRoleUpdate = async (userId, newRole) => {
    setSaving(true);
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      const storedSession = sessionStorage.getItem('supabase-auth-token');
      const accessToken = storedSession
        ? (JSON.parse(storedSession).access_token ?? supabaseKey)
        : supabaseKey;

      const res = await fetch(
        `${supabaseUrl}/rest/v1/profiles?id=eq.${userId}`,
        {
          method: 'PATCH',
          headers: {
            apikey: supabaseKey,
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            Prefer: 'return=representation',
          },
          body: JSON.stringify({ role: newRole }),
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message ?? `HTTP ${res.status}`);
      }

      const [updated] = await res.json();
      setUsers(prev => prev.map(u => (u.id === userId ? { ...u, ...updated } : u)));
      setEditTarget(null);
      showToast(`Role updated to ${getRoleInfo(newRole).label}`);
    } catch (err) {
      console.error('Role update error:', err);
      setError('Failed to update role: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  /* ── Render ── */
  return (
    <div className="um-container">

      {toast && (
        <div className="um-toast">
          <i className="bi bi-check-circle-fill" /> {toast}
        </div>
      )}

      <div className="um-header">
        <div>
          <h1 className="um-title">
            <i className="bi bi-people-fill" />
            User Management
          </h1>
          <p className="um-subtitle">
            Manage team members, assign roles and control access across your workspace.
          </p>
        </div>
      </div>
      <hr className="um-divider" />

      <div className="um-stats-row">
        <StatCard label="Total Users"  value={stats.total}        icon="bi-people"       accent="#006B54" />
        <StatCard label="Super Admins" value={stats.superAdmins}  icon="bi-shield-check" accent="#7c3aed" />
        {stats.breakdown.map(r => (
          <StatCard key={r.value} label={r.label} value={r.count} icon="bi-person-badge" accent={r.color} />
        ))}
      </div>

      <div className="um-controls">
        <div className="um-search-wrap">
          <i className="bi bi-search um-search-icon" />
          <input
            type="text"
            className="um-search"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="um-search-clear" onClick={() => setSearch('')}>
              <i className="bi bi-x" />
            </button>
          )}
        </div>

        <select
          className="um-filter-select"
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
        >
          <option value="all">All Roles</option>
          {ROLES.map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>

        <button
          className="um-btn um-btn--ghost um-refresh-btn"
          onClick={() => { setSearch(''); setRoleFilter('all'); }}
          title="Clear filters"
        >
          <i className="bi bi-arrow-counterclockwise" />
        </button>
      </div>

      {error && (
        <div className="um-alert um-alert--danger um-alert--standalone">
          <i className="bi bi-exclamation-triangle" /> {error}
        </div>
      )}

      <div className="um-table-wrap">
        {loading ? (
          <div className="um-empty">
            <div className="um-spinner" />
            <span>Loading users…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="um-empty">
            <i className="bi bi-people um-empty-icon" />
            <span>{search || roleFilter !== 'all' ? 'No users match your filters.' : 'No users found.'}</span>
          </div>
        ) : (
          <table className="um-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
                <th className="um-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const isSelf   = u.id === currentUser?.id;
                const roleInfo = getRoleInfo(u.role);
                const name     =
                  u.first_name || u.last_name
                    ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()
                    : null;

                return (
                  <tr key={u.id} className={isSelf ? 'um-row--self' : ''}>
                    <td>
                      <div className="um-user-cell">
                        <div className="um-avatar" style={{ background: roleInfo.color }}>
                          {initials(u.first_name, u.last_name, u.email)}
                        </div>
                        <div className="um-user-info">
                          <span className="um-user-name">
                            {name ?? <span className="um-muted">—</span>}
                          </span>
                          {isSelf && <span className="um-self-tag">You</span>}
                        </div>
                      </div>
                    </td>
                    <td className="um-email">{u.email ?? '—'}</td>
                    <td><RoleBadge role={u.role} /></td>
                    <td className="um-date">{formatDate(u.created_at)}</td>
                    <td>
                      <button
                        className="um-btn um-btn--edit"
                        onClick={() => setEditTarget(u)}
                        title="Change role"
                      >
                        <i className="bi bi-pencil" /> Edit Role
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="um-footer-note">
        Showing {filtered.length} of {users.length} user{users.length !== 1 ? 's' : ''} in your workspace.
      </div>

      {editTarget && (
        <EditRoleModal
          user={editTarget}
          currentUserId={currentUser?.id}
          superAdminCount={stats.superAdmins}
          onSave={handleRoleUpdate}
          onClose={() => !saving && setEditTarget(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default UserManagement;
